import { environment } from '../../../environments/environment';
import { Asset } from '../../models/image/dto/asset';
import { MediaManagerAssetFolderDto } from '../../models/image/dto/media-manager-asset-folder-dto';
import { MediaManagerAssetLibraryDto } from '../../models/image/dto/media-manager-asset-library-dto';
import { MediaManagerFolderDto } from '../../models/image/dto/media-manager-folder-dto';
import { BulkEditAssetsResponse } from '../../models/image/responses/bulk-edit-assets-response';
import { UploadFilePath } from '../../models/enum/dto/upload-file.path';
import { MediaManagerLibraryKey } from './store';

export function normalizeMediaManagerBrandId(brandId: number | null | undefined): MediaManagerLibraryKey | null {
  return typeof brandId === 'number' && Number.isFinite(brandId) && brandId > 0 ? brandId : null;
}

export function getAssetBrandId(asset: Asset | null | undefined): MediaManagerLibraryKey | null {
  const companyId = normalizeMediaManagerBrandId(asset?.companyId);
  if (companyId) return companyId;
  if (asset?.mediaClass !== UploadFilePath.AssetLibraryPath) return null;
  return normalizeMediaManagerBrandId(Number(asset.id));
}

export function getFolderBrandId(folder: MediaManagerFolderDto | null | undefined): MediaManagerLibraryKey | null {
  return normalizeMediaManagerBrandId(folder?.companyId);
}

export function getAssetFolderResponseBrandId(
  response: MediaManagerAssetFolderDto | null | undefined
): MediaManagerLibraryKey | null {
  return (
    getFolderBrandId(response?.folder) ||
    firstExistingBrandId(response?.assets?.map(asset => getAssetBrandId(asset))) ||
    firstExistingBrandId(response?.childFolders?.map(folder => getFolderBrandId(folder)))
  );
}

export function getBulkEditResponseBrandId(
  response: BulkEditAssetsResponse | null | undefined
): MediaManagerLibraryKey | null {
  return (
    getAssetFolderResponseBrandId(response?.updatedTargetFolder) ||
    firstExistingBrandId(response?.updatedAssets?.map(asset => getAssetBrandId(asset))) ||
    firstExistingBrandId(response?.updatedAssetFolders?.map(folder => getFolderBrandId(folder)))
  );
}

export function findBrandIdForAsset(
  asset: Asset | null | undefined,
  assetLibrariesByBrandId: Map<MediaManagerLibraryKey, MediaManagerAssetLibraryDto>
): MediaManagerLibraryKey | null {
  const explicitBrandId = getAssetBrandId(asset);
  if (explicitBrandId) return explicitBrandId;
  const assetUniqueId = asset?.getUniqueIdentifier();
  if (!assetUniqueId) return null;
  return findBrandIdForAssetUniqueId(assetUniqueId, assetLibrariesByBrandId);
}

export function findBrandIdForAssetUniqueId(
  assetUniqueId: string | null | undefined,
  assetLibrariesByBrandId: Map<MediaManagerLibraryKey, MediaManagerAssetLibraryDto>
): MediaManagerLibraryKey | null {
  const normalizedAssetUniqueId = assetUniqueId?.trim();
  if (!normalizedAssetUniqueId) return null;
  const brandIds = Array.from(assetLibrariesByBrandId.entries())
    .filter(([, assetLibrary]) => {
      return assetLibrary?.assets?.some(asset => asset?.getUniqueIdentifier() === normalizedAssetUniqueId);
    })
    .map(([brandId]) => brandId);
  return brandIds.length === 1 ? brandIds[0] : null;
}

export function findBrandIdForFolderId(
  folderId: string | null | undefined,
  assetLibrariesByBrandId: Map<MediaManagerLibraryKey, MediaManagerAssetLibraryDto>
): MediaManagerLibraryKey | null {
  const normalizedFolderId = folderId?.trim();
  if (!normalizedFolderId || normalizedFolderId === 'ROOT') return null;
  const brandIds = Array.from(assetLibrariesByBrandId.entries())
    .filter(([, assetLibrary]) => {
      return assetLibrary?.assetFolders?.some(folder => folder?.id === normalizedFolderId);
    })
    .map(([brandId]) => brandId);
  return brandIds.length === 1 ? brandIds[0] : null;
}

export function findBrandIdForAssetFolderResponse(
  response: MediaManagerAssetFolderDto | null | undefined,
  assetLibrariesByBrandId: Map<MediaManagerLibraryKey, MediaManagerAssetLibraryDto>
): MediaManagerLibraryKey | null {
  const explicitBrandId = getAssetFolderResponseBrandId(response);
  if (explicitBrandId) return explicitBrandId;
  return (
    findBrandIdForFolderId(response?.folder?.id, assetLibrariesByBrandId) ||
    findBrandIdForFolderId(response?.folder?.parentFolderId, assetLibrariesByBrandId) ||
    firstExistingBrandId(response?.assets?.map(asset => findBrandIdForAsset(asset, assetLibrariesByBrandId))) ||
    firstExistingBrandId(
      response?.childFolders?.map(folder => findBrandIdForFolderId(folder?.id, assetLibrariesByBrandId))
    )
  );
}

export function findBrandIdForBulkEditResponse(
  response: BulkEditAssetsResponse | null | undefined,
  assetLibrariesByBrandId: Map<MediaManagerLibraryKey, MediaManagerAssetLibraryDto>
): MediaManagerLibraryKey | null {
  const explicitBrandId = getBulkEditResponseBrandId(response);
  if (explicitBrandId) return explicitBrandId;
  return (
    findBrandIdForAssetFolderResponse(response?.updatedTargetFolder, assetLibrariesByBrandId) ||
    firstExistingBrandId(response?.updatedAssets?.map(asset => findBrandIdForAsset(asset, assetLibrariesByBrandId))) ||
    firstExistingBrandId(
      response?.updatedAssetFolders?.map(folder => findBrandIdForFolderId(folder?.id, assetLibrariesByBrandId))
    )
  );
}

export function resolveMediaManagerBrandIdWithActiveFallback(
  resolvedBrandId: MediaManagerLibraryKey | null | undefined,
  activeBrandId: MediaManagerLibraryKey | null | undefined,
  reason: string
): MediaManagerLibraryKey | null {
  const normalizedResolvedBrandId = normalizeMediaManagerBrandId(resolvedBrandId);
  if (normalizedResolvedBrandId) return normalizedResolvedBrandId;
  const normalizedActiveBrandId = normalizeMediaManagerBrandId(activeBrandId);
  if (normalizedActiveBrandId) {
    if (!environment.production) {
      console.warn(`MediaManager could not resolve brand for '${reason}'. Falling back to active brand.`);
    }
    return normalizedActiveBrandId;
  }
  return null;
}

function firstExistingBrandId(
  brandIds: Array<MediaManagerLibraryKey | null | undefined> | null | undefined
): MediaManagerLibraryKey | null {
  return brandIds?.find(brandId => brandId !== null && brandId !== undefined) ?? null;
}
